import {
  CarouselPortraitProgress,
  GeneralImage,
  ModuleBase,
} from '@/components';
import {
  ImageModel,
  ModuleBaseModel,
  ModuleSectionPropsModel,
} from '@/types';
import Flicking, { ChangedEvent, WillChangeEvent } from '@egjs/react-flicking';

import { useEffect, useRef, useState } from 'react';
import tailwindConfig from '@/tailwind.screens';
import SectionHeading from '@/components/general/SectionHeading';

type ImageFocusWithGalleryModuleModel = ModuleBaseModel<
  {
    imageSlides: ImageModel[];
  } & ModuleSectionPropsModel
>;

const ImageFocusWithGalleryModule = (data: ImageFocusWithGalleryModuleModel) => {
  const {
    props: { imageSlides },
  } = data;

  const [defaultIndex, setDefaultIndex] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [flickMarginLeft, setFlickMarginLeft] = useState(0);

  const carousel = useRef<Flicking>(null);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)) {
        setDefaultIndex(1);
        setCurrentSlide(1);
      } else {
        setDefaultIndex(0);
        setCurrentSlide(0);
      }

      setFlickMarginLeft(0);
      carousel.current?.element.querySelectorAll('.card-panel').forEach((e) => {
        (e as HTMLElement).style.removeProperty('margin-left');
        (e as HTMLElement).style.removeProperty('margin-right');
      });
      carousel.current?.element.querySelectorAll('.card-panel').forEach((e) => {
        (e as HTMLElement).style.removeProperty('transform');
      });

      carousel.current?.resize();
      setTimeout(() => {
        onCarouselReady();
      }, 500);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCarouselChanged = (event: ChangedEvent<Flicking>) => {
    setCurrentSlide(event.index);
  };

  const onCarouselReady = () => {
    if (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)) {
      setFlickMarginLeft(Math.min(Math.max((document.body.clientWidth / 12.8) * -1, -150), 0));
    } else {
      setFlickMarginLeft(0);
    }
    setTimeout(() => {
      onCarouselMove();
    }, 100);
  };

  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const smoothstep = (t: number) => t * t * (3 - 2 * t);

  let raf = 0;

  const onCarouselMove = () => {
    const flicking = carousel.current;
    if (!flicking || window.innerWidth < parseInt(tailwindConfig.screens.lg, 10)) return;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const isLg = window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10);

      flicking.visiblePanels.forEach((panel) => {
        const target = panel.element as HTMLElement;

        if (!isLg) {
          target.style.transform = '';
          return;
        }

        const progress = panel.outsetProgress + 1;

        // ✅ focal point = left-of-center panel (from your logged default progress values)
        const focal = 0.6391042204995694;

        // how wide the effect should be around the focal point
        const band = 0.32; // similar to your old (1 - 0.68)

        const dist = Math.abs(progress - focal);
        const t = smoothstep(clamp01(1 - dist / band)); // 1 at focal, 0 outside band

        // your original tunables
        const scaleBase = 1;
        const scaleMax = 1.36;

        const translateYBase = 0;
        const translateYMax = 8;

        const panelMarginRatio = 26; // higher = less margin
        const panelMarginMax = Math.min(document.body.clientWidth / panelMarginRatio, 250);

        const currentScale = lerp(scaleBase, scaleMax, t);
        const translateY = lerp(translateYBase, translateYMax, t);
        const panelMargin = lerp(0, panelMarginMax, t);

        target.style.transform = `scale(${currentScale}) translateY(-${translateY}%)`;

        const buttons = target.querySelector('.buttons');
        if (buttons instanceof HTMLElement) {
          const inv = 1 / currentScale;
          buttons.style.transform = `scale(${inv})`;
          buttons.style.transformOrigin = 'top right';
        }

        const cardPanel = target.closest('.card-panel');
        if (cardPanel instanceof HTMLElement) {
          cardPanel.style.marginLeft = `${panelMargin}px`;
          cardPanel.style.marginRight = `${panelMargin}px`;
        }
      });
    });
  };

  if (!imageSlides || imageSlides.length === 0) {
    return null;
  }

  return (
    <ModuleBase data={data} className="overflow-hidden">
      <div className="container flex min-h-[8vw] justify-end pt-1 lg:pr-16">
        <div className="w-full lg:w-1/2 xl:pr-16">
          <SectionHeading
            className="!gap-6 py-0 [&_.heading]:!text-[22px] [&_.heading]:!leading-[32px] [&_.heading]:!font-light [&_.heading]:xl:!text-[40px] [&_.heading]:xl:!leading-[48px]"
            data={data.props}
          />
        </div>
      </div>

      <Flicking
        ref={carousel}
        preventClickOnDrag
        preventDefaultOnDrag
        circular
        interruptable={false}
        // moveType={['strict', { count: 1 }]}
        threshold={1}
        align={'center'}
        hideBeforeInit={true}
        defaultIndex={defaultIndex}
        autoResize={false}
        onReady={onCarouselReady}
        onChanged={onCarouselChanged}
        onMove={onCarouselMove}
        className="mt-7 mb-5 w-full !overflow-visible lg:mt-[35px] lg:mb-0"
        style={{ 'margin-left': `${flickMarginLeft}px` }}
      >
        {imageSlides &&
          imageSlides.map((image, index) => (
            <div key={index} className="card-panel h-full w-9/12 lg:w-[22%]">
              <div className="carousel-with-focus-wrapper card-content flex h-full w-full items-center justify-center p-3">
                <GeneralImage
                  image={image}
                  className="fill-height [&_.general-image]:w-full"
                  widths={{ xs: 250, sm: 310, md: 400, lg: 400, xl: 462 }}
                  heights={{ xs: 375, sm: 465, md: 600, lg: 550, xl: 693 }}
                />
              </div>
            </div>
          ))}
      </Flicking>

      <div className="container flex justify-end pt-6">
        <div className="w-full lg:w-1/2">
          <CarouselPortraitProgress carousel={carousel} />
        </div>
      </div>
    </ModuleBase>
  );
};

export default ImageFocusWithGalleryModule;
